import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { add, format } from "date-fns";
import { toast } from "react-toastify";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,  
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, SmartDisplay } from "@mui/icons-material";

import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { IAssignmentReminderDialogProps } from "../types/crew-dashboard";
import { StreamResponse } from "../CrewDashboard";
import { AxiosError } from "axios";
import { tokens } from "../../../theme";

interface StreamProps {
  title: string;
  date: string;
  start: string;
};

interface FormProps {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const AssignmentReminderDialog: React.FC<IAssignmentReminderDialogProps> = ({
  notification,
  setShowAssignmentReminderDialog,
  showAssignmentReminderDialog,
  selectedStream
}) => {
  const [stream, setStream] = useState<StreamProps | null>(null);
  const [streamLoading, setStreamLoading] = useState<boolean>(false);
  const [streamError, setStreamError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormProps>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const resetForm = () => {
    setFormData({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    });
  };

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const closeAndReset = () => {
    resetForm();
    setShowAssignmentReminderDialog(false);
  };

  const handleRemindLater = async () => {
    const { days, hours, minutes, seconds } = formData;
    try {
      const newDueDate = add(new Date(), {
        days: days,
        hours: hours,
        minutes: minutes,
        seconds: seconds
      });

      const formatedDueDate = `${format(newDueDate, 'yyyy-MM-dd')}T${format(newDueDate, 'HH:mm:ss')}+02:00`;

      const payload = {
        due_date_time: formatedDueDate
      };

      const reminder = await apiService.patch(
        `/api/notifications/notification/${notification}/`,
        payload
      );

      console.log({ reminder });

      toast.success(`You will be reminded about this assignment on ${format(newDueDate, 'dd MMM yyyy, H:m')}.`);
      closeAndReset();
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to stall notification. Please try again.";
        toast.error(errorMessage);
      } else {
        console.error("Failed to stall notification:", error);
      }
    } finally {
      closeAndReset();
    }
  };

  const handlePeriodChange = (field: keyof FormProps, value: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const fetchStream = async () => {
      setStreamLoading(true);
      setStreamError(null);
      try {
        const strm = await apiService.get<StreamResponse>(
          `/api/streams/stream/${selectedStream}/`);
        
        console.log({ strm });
        setStream({
          title: strm.data.title,
          date: strm.data.date,
          start: strm.data.start
        });
      } catch (error) {
        if (error instanceof AxiosError) {
          const errorMessage =
            error.response?.data?.message ||
            "Failed to load streams. Please try again.";
          setStreamError(errorMessage);
        } else {
          console.error("Failed to fetch selected stream:", error);
        }      
        setStream(null);
      } finally {
        setStreamLoading(false);
      }
    };

    if (selectedStream) {
      fetchStream();
    }
  }, [selectedStream]);

  if (!notification) return null;

  return (
    <Dialog
      open={showAssignmentReminderDialog}
      onClose={closeAndReset}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        Set A Reminder
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ my: 1 }}>
          {streamLoading && (
            <CircularProgress color={"primary"} size={16} />
          )}
          {streamError ? (
            <Alert severity="error">{streamError}</Alert>
          ) : !stream ? (
            <Chip
              label={
                <i>Could not fetch stream. Try again.</i>
              }
              size="small"
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                mb: 2,
                borderColor: colors.primary[800],
              }}
            />
          ) : (
            <>
              <Box
                display={'flex'}
                sx={{
                  backgroundColor: colors.primary[400],
                  padding: 1,
                  borderRadius: 2
                }}
                alignItems={'center'}
                mb={2}
                gap={1}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    backgroundColor: colors.primary[900],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <SmartDisplay sx={{ color: colors.primary[400], fontSize: 32 }} />
                </Box>
                <Box sx={{ color: '#FFF' }}>
                  <Typography variant={'caption'}>Booking Confirmed</Typography>
                  <Typography variant={'subtitle1'} fontWeight={600}>{stream.title}</Typography>
                  <Typography variant={'caption'}>
                    {`${format(stream.date, 'dd MMM yyyy')} - ${stream.start}`}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ my: 2 }} variant={'subtitle2'}>Remind me in:</Typography>

              <Grid container spacing={2}>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label={'Days'}
                    type="number"
                    inputProps={{
                      min: 0
                    }}
                    value={formData.days}
                    onChange={(e) =>
                      handlePeriodChange("days", Number(e.target.value))
                    }
                    variant="outlined"
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label={'Hours'}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 23
                    }}
                    value={formData.hours}
                    onChange={(e) =>
                      handlePeriodChange("hours", Number(e.target.value))
                    }
                    variant="outlined"
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label={'Minutes'}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 59
                    }}
                    value={formData.minutes}
                    onChange={(e) =>
                      handlePeriodChange("minutes", Number(e.target.value))
                    }
                    variant="outlined"
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label={'Seconds'}
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 59
                    }}
                    value={formData.seconds}
                    onChange={(e) =>
                      handlePeriodChange("seconds", Number(e.target.value))
                    }
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              <Button
                fullWidth
                sx={{
                  color: '#FFF',
                  mt: 1,
                  backgroundColor: colors.primary[400]
                }}
                onClick={handleRemindLater}>
                Remind Later  
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeAndReset}
          sx={{
            color: colors.primary[100],
            backgroundColor: colors.primary[800],
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentReminderDialog;
